import { Alert, DatePicker, Form, Modal, Select, Typography, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import { TYPE_DEPARTMENT } from 'constants/common';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import {
  departmentFileSlug,
  extractErrorMessage,
  parseFilename,
  triggerDownload,
} from 'pages/attendance-manager/component/export-all-modal';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  month: Dayjs;
  department?: string;
  holidays?: Dayjs[];
}

const DEPARTMENT_OPTIONS = [
  { value: '', label: 'Tất cả bộ phận' },
  { value: TYPE_DEPARTMENT.HR, label: 'Nhân sự' },
  { value: TYPE_DEPARTMENT.TECHNICAL, label: 'Kĩ thuật' },
  { value: TYPE_DEPARTMENT.MAKEUP, label: 'Makeup' },
  { value: TYPE_DEPARTMENT.MC, label: 'MC' },
  { value: TYPE_DEPARTMENT.IDOL, label: 'Creator' },
  { value: TYPE_DEPARTMENT.CONTENT, label: 'Content' },
  { value: TYPE_DEPARTMENT.STYLE_LIST, label: 'Style List' },
  { value: TYPE_DEPARTMENT.DANCE_TEACHER, label: 'Dance Teacher' },
  { value: TYPE_DEPARTMENT.MANAGER, label: 'QL Vận Hành' },
];

/** Đầu tháng (≤ ngày 10) thường là lúc làm lương tháng trước. */
const defaultMonth = () => (dayjs().date() <= 10 ? dayjs().subtract(1, 'month') : dayjs());

/**
 * Xuất "Bảng công & lương" tháng ra Excel: lưới ai nghỉ / trễ ngày nào, tổng
 * hợp công, chuyên cần, tăng ca và sheet tính lương có công thức.
 */
const ExportTimesheetModal: React.FC<Props> = ({ open, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<{ standardDays: number; note: string } | null>(null);
  const month: Dayjs | undefined = Form.useWatch('month', form);
  const monthStr = month ? dayjs(month).format('YYYY-MM') : undefined;

  // Đổi tháng → điền sẵn ngày lễ cố định + hiện công chuẩn.
  useEffect(() => {
    if (!open || !monthStr) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await AxiosUtils.get(API.payrollTimesheetDefaults, { month: monthStr });
        if (cancelled) return;
        const d = res?.data;
        setInfo({ standardDays: d?.standardDays, note: d?.standardDaysNote });
        form.setFieldValue(
          'holidays',
          (d?.holidays || []).map((h: string) => dayjs(h)),
        );
      } catch {
        if (!cancelled) setInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, monthStr, form]);

  const handleClose = () => {
    if (loading) return;
    form.resetFields();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const m = dayjs(values.month).format('YYYY-MM');
    const params: Record<string, string> = {
      month: m,
      // Luôn gửi (kể cả rỗng) để server dùng đúng danh sách admin đã chọn.
      holidays: (values.holidays || [])
        .map((d) => dayjs(d).format('YYYY-MM-DD'))
        .filter((d) => d.startsWith(m))
        .sort()
        .join(','),
    };
    if (values.department) params.department = values.department;
    setLoading(true);
    try {
      const response: any = await AxiosUtils.get(API.payrollTimesheetExport, params, undefined, {
        responseType: 'blob',
        timeout: 120_000,
      });
      const fallback = `bang-cong-luong-${m}${
        values.department ? `-${departmentFileSlug(values.department)}` : ''
      }.xlsx`;
      const filename = parseFilename(response?.headers?.['content-disposition'], fallback);
      const blob = new Blob([response.data], {
        type:
          response?.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerDownload(blob, filename);
      notification.success({ message: 'Đã tải file', description: filename });
      handleCloseAfterDone();
    } catch (err) {
      notification.error({
        message: 'Không xuất được file',
        description: await extractErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAfterDone = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Xuất bảng công & lương tháng"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={!loading}
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
        File Excel gồm: lưới ngày cho biết ai nghỉ, đi trễ ngày nào (rê chuột xem giờ vào/ra); tổng
        hợp công, chuyên cần, tăng ca; sheet Tính lương có sẵn công thức — điền lương cơ bản là ra
        thực lĩnh, sửa ký hiệu ngày là tự tính lại.
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{ month: defaultMonth(), department: '' }}
      >
        <Form.Item
          name="month"
          label="Tháng"
          rules={[{ required: true, message: 'Chọn tháng' }]}
        >
          <DatePicker
            picker="month"
            allowClear={false}
            size="large"
            style={{ width: '100%' }}
            format="MM/YYYY"
            disabledDate={(d) => d.isAfter(dayjs(), 'month')}
          />
        </Form.Item>
        <Form.Item name="department" label="Bộ phận">
          <Select size="large" options={DEPARTMENT_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="holidays"
          label="Ngày lễ trong tháng (có lương)"
          extra="Đã điền sẵn ngày lễ cố định. Thêm Tết âm lịch, Giỗ Tổ, ngày liền kề Quốc khánh, ngày nghỉ bù nếu có."
        >
          <DatePicker
            multiple
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder="Không có ngày lễ"
            disabledDate={(d) => !month || !d.isSame(month, 'month')}
            defaultPickerValue={month}
          />
        </Form.Item>
        {info && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Công chuẩn tháng này: ${info.standardDays} công`}
            description={`${info.note}. Đổi ở Điểm Sao → Cấu hình (dùng chung cho lương).`}
          />
        )}
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <CustomButton
            text="Hủy"
            bgColor="primary-grey"
            onClick={handleClose}
            disabled={loading}
            className="mr-10"
          />
          <CustomButton
            text={loading ? 'Đang tạo file...' : 'Tải file Excel'}
            bgColor="primary-green"
            htmlType="submit"
            loading={loading}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExportTimesheetModal;
